import { Button } from "@/components/ui/button";
import { ArrowRight, Briefcase, UserRound, Sparkles } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden grain"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-white via-slate-50 to-white"
      />
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-100/60 blur-3xl -z-10"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-teal-100/50 blur-3xl -z-10"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-28 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white text-xs font-mono-ui uppercase tracking-[0.2em] text-slate-600">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
              AI-Powered Recruitment · Europe
            </div>
            <h1
              data-testid="hero-headline"
              className="font-display font-black mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.05]"
            >
              Hire Top IT & Engineering Talent{" "}
              <span className="text-blue-600">Across Europe.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              Vetted software engineers, SAP consultants, AI specialists and
              executives in Germany, Netherlands, Romania, Poland, Bulgaria and
              Remote Europe — matched by AI, screened by senior recruiters,
              delivered in days, not months.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4 max-w-2xl">
              <a
                href="#employers"
                data-testid="hero-employer-cta"
                className="group relative rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
                    <Briefcase className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                      For Employers
                    </div>
                    <div className="font-display font-bold text-slate-900 mt-0.5">
                      Hire talent fast
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600 flex items-center gap-1 group-hover:text-blue-700">
                  Submit a role <ArrowRight className="w-4 h-4" />
                </div>
              </a>
              <a
                href="#candidates"
                data-testid="hero-candidate-cta"
                className="group relative rounded-2xl border border-slate-200 bg-white p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 grid place-items-center">
                    <UserRound className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                      For Candidates
                    </div>
                    <div className="font-display font-bold text-slate-900 mt-0.5">
                      Find your next role
                    </div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600 flex items-center gap-1 group-hover:text-teal-700">
                  Upload your CV <ArrowRight className="w-4 h-4" />
                </div>
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-slate-500">
              <div>
                <span className="text-2xl font-display font-black text-slate-900">
                  2,400+
                </span>
                <div className="uppercase tracking-[0.2em]">Placements</div>
              </div>
              <div className="w-px h-8 bg-slate-200" />
              <div>
                <span className="text-2xl font-display font-black text-slate-900">
                  18d
                </span>
                <div className="uppercase tracking-[0.2em]">Avg. time-to-hire</div>
              </div>
              <div className="w-px h-8 bg-slate-200" />
              <div>
                <span className="text-2xl font-display font-black text-slate-900">
                  92%
                </span>
                <div className="uppercase tracking-[0.2em]">12-mo retention</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
                alt="Modern European office"
                className="w-full h-[460px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl p-4 border border-white/60">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                  Live shortlist · Berlin
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <div>
                    <div className="font-display font-bold text-slate-900">
                      Senior SAP S/4HANA Consultant
                    </div>
                    <div className="text-xs text-slate-500">
                      14 vetted candidates · AI-ranked
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-slate-900 hover:bg-slate-800 text-white"
                    data-testid="hero-shortlist-cta"
                  >
                    View
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
